import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AvatarSmall, CouponCodeField, Tooltip } from '../../components';
import { ensureUser } from '../../util/data';
import CompactCalendar from './CompactCalendar';
import { useBookedTimeSlots } from './hooks/useBookedTimeSlots';
import {
  getAllAvailableDates,
  getAvailableDatesForSessionType,
  getSessionTypesForDate,
  filterSessionTypesWithFutureAvailability,
  formatDateKeyInTimezone,
  getTimeSlotsForSessionType,
  getBookingTimestamps,
  formatTimeRange,
  parseTime,
} from './utils/availabilityUtils';
import { calculateCampDates } from '../ProviderPage/utils/campUtils';
import {
  filterAvailableTimeSlots,
  getSessionSpotsLeft,
  getTimeSlotAvailability,
  normalizeSessionTypeId,
} from './utils/bookingUtils';
import { normalizeCoachifyImageUrl } from '../../util/coachifyPublicImageUrl';
import {
  resolveCoachTimezone,
  resolveCustomerTimezone,
  formatForViewer,
} from '../../util/bookingTime';
import {
  calculateBookingFeeBreakdownFromDollars,
  formatCentsAsDollars,
} from '../../util/bookingFee';
import { PROCESSING_FEE_LABEL, platformFeeLabel } from '../../util/processingFee';
import { parse, stringify } from '../../util/urlHelpers';
import {
  createSubscriptionCheckoutSessionApi,
  getPublicSubscriptionTiersByListingApi,
  getUserListingApi,
  validateBookingCreditsApi,
} from '../../util/api';
import GiftSubscriptionModal from './GiftSubscriptionModal';
import { billingIntervalSuffix } from '../../util/subscriptionBilling';

import css from './CustomBookingPanel.module.css';

const BOOKING_PANEL_TABS = ['sessions', 'products', 'subscribe'];

const tabFromSearch = search => {
  const params = parse(search);
  const raw = params.tab;
  if (typeof raw !== 'string') return null;
  const lower = raw.toLowerCase();
  if (lower === 'subscribe' || lower === 'subscriptions' || lower === 'subscription') {
    return 'subscribe';
  }
  if (lower === 'products') return 'products';
  if (lower === 'sessions') return 'sessions';
  return null;
};

const CustomBookingPanel = (props) => {
  const {
    className,
    listing,
    author,
    onSubmit,
    isOwnListing,
    children,
    sessionTypes = [],
    availabilityData = { camps: {}, schedulingBlocks: {}, scheduledDates: [] },
    coachTimezone: coachTimezoneProp = null,
    listingGuestRequirements = '',
    listingAccessibility = '',
    rating = 0,
    reviewCount = 0,
    loading = false,
    selectedMonth: selectedMonthProp,
    onMonthChange,
    currentUser,
    listingIdOverride = null,
    sessionsUnavailable = false,
  } = props;

  // State
  const [guestCount, setGuestCount] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [guestNames, setGuestNames] = useState([]);
  const [isGuestNamesExpanded, setIsGuestNamesExpanded] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const defaultTab = sessionsUnavailable ? 'subscribe' : 'sessions';
  const [activeTab, setActiveTab] = useState(
    () => tabFromSearch(location.search) || defaultTab
  );
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);
  const [tiersError, setTiersError] = useState('');
  const [subscribeError, setSubscribeError] = useState('');
  const [subscribingTierId, setSubscribingTierId] = useState(null);
  const [resolvedListingId, setResolvedListingId] = useState(listing?.id?.uuid || null);
  const [useCreditForBooking, setUseCreditForBooking] = useState(false);
  const [creditValidation, setCreditValidation] = useState(null);
  const [loadingCreditValidation, setLoadingCreditValidation] = useState(false);
  const sessionSectionRef = useRef(null);
  const availableTimesRef = useRef(null);
  const panelRef = useRef(null);
  const [fallbackSelectedMonth] = useState(() => new Date());
  const selectedMonth = selectedMonthProp ?? fallbackSelectedMonth;

  useEffect(() => {
    const tab = tabFromSearch(location.search);
    if (sessionsUnavailable) {
      setActiveTab(prev => (prev === 'subscribe' ? prev : 'subscribe'));
      return;
    }
    const nextTab = tab || 'sessions';
    setActiveTab(prev => (prev === nextTab ? prev : nextTab));
  }, [location.search, sessionsUnavailable]);

  // Scroll booking panel into view when arriving via ?tab=subscribe (or products).
  useEffect(() => {
    const tab = tabFromSearch(location.search);
    if (!tab || tab === 'sessions') {
      return undefined;
    }

    const scrollToPanel = () => {
      const el = panelRef.current;
      if (!el) {
        return false;
      }
      const topbarOffset = 96;
      const top = el.getBoundingClientRect().top + window.pageYOffset - topbarOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      return true;
    };

    let retryTimer;
    const timer = setTimeout(() => {
      if (!scrollToPanel()) {
        retryTimer = setTimeout(scrollToPanel, 200);
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      clearTimeout(retryTimer);
    };
  }, [location.search]);

  const onBookingTabChange = useCallback(
    tab => {
      if (!BOOKING_PANEL_TABS.includes(tab)) return;
      if (sessionsUnavailable && tab !== 'subscribe') return;
      setActiveTab(tab);
      const params = { ...parse(location.search) };
      if (tab === 'sessions') {
        delete params.tab;
      } else {
        params.tab = tab;
      }
      const qs = stringify(params);
      const nextSearch = qs ? `?${qs}` : '';
      if (nextSearch !== location.search) {
        history.replace({
          pathname: location.pathname,
          search: nextSearch,
          hash: location.hash,
        });
      }
    },
    [history, location.hash, location.pathname, location.search, sessionsUnavailable]
  );

  // Fetch session types and availability data
  const listingId = listing?.id?.uuid;

  // Coach/listing timezone for availability (author profile → API → listing, not customer browser)
  const timeZone = resolveCoachTimezone({
    user: author,
    listing,
    coachTimezone: coachTimezoneProp,
  });
  const customerTimeZone = useMemo(
    () => resolveCustomerTimezone({ user: currentUser }),
    [currentUser]
  );
  const dateKeyForSelection = useCallback(
    day =>
      formatDateKeyInTimezone(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        day,
        customerTimeZone
      ),
    [selectedMonth, customerTimeZone]
  );
  // Customer-local calendar day (YYYY-MM-DD) for slot queries and booking
  const selectedDateString = useMemo(() => {
    if (!selectedDate) return null;
    return dateKeyForSelection(selectedDate);
  }, [selectedDate, dateKeyForSelection]);

  // Always fetch booked slots for all session types on this listing.
  const {
    bookedSlotsMap,
    aggregateBookedSlotsMap,
    loading: loadingBookedSlots,
  } = useBookedTimeSlots(sessionsUnavailable ? null : listingId, selectedDateString);

  // Filter session types to only show those with future availability
  const availableSessionTypesFiltered = useMemo(
    () =>
      filterSessionTypesWithFutureAvailability(
        sessionTypes,
        availabilityData,
        timeZone,
        customerTimeZone
      ),
    [sessionTypes, availabilityData, timeZone, customerTimeZone]
  );

  // Get enabled dates (filtered by selected session type if applicable)
  const enabledDates = useMemo(() => {
    const dataSource = selectedSessionType ? [selectedSessionType] : availableSessionTypesFiltered;
    const dates = getAllAvailableDates(dataSource, availabilityData, timeZone, customerTimeZone);
    const today = moment()
      .tz(customerTimeZone || 'Etc/UTC')
      .startOf('day');

    // Filter out past dates
    const filteredDates = new Set();
    dates.forEach((dateStr) => {
      if (moment.tz(dateStr, 'YYYY-MM-DD', customerTimeZone || 'Etc/UTC').isSameOrAfter(today)) {
        filteredDates.add(dateStr);
      }
    });
    return filteredDates;
  }, [
    availableSessionTypesFiltered,
    selectedSessionType,
    availabilityData,
    timeZone,
    customerTimeZone,
  ]);

  // Get session info map for calendar display
  // If selectedSessionType is set, show only that session name
  // Otherwise, show first session name + count of other sessions
  const dateSessionsMap = useMemo(() => {
    const map = new Map();
    const today = moment()
      .tz(customerTimeZone || 'Etc/UTC')
      .startOf('day');

    enabledDates.forEach((dateStr) => {
      if (!moment.tz(dateStr, 'YYYY-MM-DD', customerTimeZone || 'Etc/UTC').isSameOrAfter(today)) {
        return;
      }

      if (selectedSessionType) {
        // When a session is selected, only show that session name
        map.set(dateStr, {
          displayName: selectedSessionType.sessionName,
        });
      } else {
        // When no session is selected, show all available sessions for this date
        const sessionsForDate = getSessionTypesForDate(
          dateStr,
          availableSessionTypesFiltered,
          availabilityData,
          timeZone,
          customerTimeZone
        );

        if (sessionsForDate.length > 0) {
          const firstSession = sessionsForDate[0];
          const otherCount = sessionsForDate.length - 1;
          map.set(dateStr, {
            displayName:
              otherCount > 0
                ? `${firstSession.sessionName} +${otherCount}`
                : firstSession.sessionName,
          });
        }
      }
    });

    return map;
  }, [
    enabledDates,
    selectedSessionType,
    availableSessionTypesFiltered,
    availabilityData,
    timeZone,
    customerTimeZone,
  ]);

  // Get session types available for the selected date
  const availableSessionTypes = useMemo(() => {
    if (!selectedDate) return [];
    const dateString = dateKeyForSelection(selectedDate);
    return getSessionTypesForDate(
      dateString,
      availableSessionTypesFiltered,
      availabilityData,
      timeZone,
      customerTimeZone
    );
  }, [
    selectedDate,
    dateKeyForSelection,
    availableSessionTypesFiltered,
    availabilityData,
    timeZone,
    customerTimeZone,
  ]);

  const ensuredAuthor = ensureUser(author);

  useEffect(() => {
    if (!selectedSessionType?._id || !currentUser?.id?.uuid) {
      setCreditValidation(null);
      setUseCreditForBooking(false);
      return;
    }
    let cancelled = false;
    setLoadingCreditValidation(true);
    validateBookingCreditsApi({ sessionTypeId: selectedSessionType._id })
      .then(res => {
        if (!cancelled) {
          setCreditValidation(res || null);
          if (!(res && res.canUseCredits)) {
            setUseCreditForBooking(false);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCreditValidation(null);
          setUseCreditForBooking(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCreditValidation(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSessionType?._id, currentUser?.id?.uuid]);

  const { title, price } = listing.attributes;
  const formattedPrice = price?.amount ? `$${(price.amount / 100).toFixed(2)}` : '$0.00';
  const subscriptions = useMemo(() => {
    const fallbackColors = ['teal', 'amber', 'purple'];
    return (Array.isArray(subscriptionTiers) ? subscriptionTiers : [])
      .filter(tier => tier?.isActive !== false && tier?.archived !== true && tier?.isArchived !== true)
      .slice()
      .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
      .map((tier, index) => {
        const rawColor = typeof tier?.color === 'string' ? tier.color.toLowerCase() : '';
        const color = rawColor.includes('amber')
          ? 'amber'
          : rawColor.includes('purple')
            ? 'purple'
            : rawColor.includes('teal')
              ? 'teal'
              : fallbackColors[index % fallbackColors.length];
        const billingType = tier?.billingType === 'one_time' ? 'one_time' : 'recurring';
        const billingInterval = tier?.billingInterval || 'month';
        const customIntervalUnit = tier?.customIntervalUnit || null;
        const customIntervalCount = tier?.customIntervalCount || null;
        const benefits = Array.isArray(tier?.benefits)
          ? tier.benefits.filter(Boolean).map(text => ({ icon: 'play', text }))
          : [];
        const credits = tier?.credits;
        if (credits?.enabled && Number(credits?.creditsPerPeriod || 0) > 0) {
          const creditCount = credits.creditsPerPeriod;
          // Bare cadence text (no leading slash) for "N credits / <cadence>" --
          // billingIntervalSuffix returns "/mo"-style strings meant to follow
          // a price, not a credit count, so this mirrors it rather than
          // reusing it directly.
          const creditFrequency = credits?.frequency || billingInterval;
          const creditSuffix =
            creditFrequency === 'year'
              ? 'yr'
              : creditFrequency === 'week'
                ? 'wk'
                : creditFrequency === 'custom'
                  ? `${customIntervalCount || 1} ${customIntervalUnit || 'month'}${
                      (customIntervalCount || 1) === 1 ? '' : 's'
                    }`
                  : 'mo';
          const creditLabel =
            billingType === 'one_time'
              ? `${creditCount} credit${Number(creditCount) === 1 ? '' : 's'} included`
              : `${creditCount} credit${Number(creditCount) === 1 ? '' : 's'} / ${creditSuffix}`;
          benefits.push({
            icon: 'gift',
            text: creditLabel,
          });
        }

        return {
          id: tier?._id || tier?.id,
          name: tier?.name || 'Subscription Tier',
          description: tier?.description || 'Membership perks and exclusive benefits',
          price: Number(tier?.priceAmount || 0) / 100,
          billingType,
          billingInterval,
          customIntervalUnit,
          customIntervalCount,
          color,
          popular: Boolean(tier?.featured),
          subscribers: Number(
            tier?.activeSubscriberCount ?? tier?.memberCount ?? tier?.subscriberCount ?? 0
          ),
          image: normalizeCoachifyImageUrl(tier?.imageUrl || ''),
          perks: benefits,
        };
      })
      .filter(tier => !!tier.id);
  }, [subscriptionTiers]);

  useEffect(() => {
    let isMounted = true;

    const resolveListingId = async () => {
      if (listingIdOverride) {
        setResolvedListingId(listingIdOverride);
        return;
      }

      if (listing?.id?.uuid) {
        setResolvedListingId(listing.id.uuid);
        return;
      }

      const authorId = author?.id?.uuid;
      if (!authorId) {
        setResolvedListingId(null);
        return;
      }

      try {
        const listingResponse = await getUserListingApi({ userId: authorId });
        if (isMounted) {
          setResolvedListingId(listingResponse?.data?.listingId || null);
        }
      } catch (error) {
        console.error('Failed to resolve listing for subscriptions:', error);
        if (isMounted) {
          setResolvedListingId(null);
        }
      }
    };

    resolveListingId();
    return () => {
      isMounted = false;
    };
  }, [listing?.id?.uuid, author?.id?.uuid, listingIdOverride]);

  useEffect(() => {
    let isMounted = true;
    const shouldLoadTiers =
      resolvedListingId && (sessionsUnavailable || activeTab === 'subscribe');
    if (!shouldLoadTiers) {
      return undefined;
    }

    const fetchSubscriptionTiers = async () => {
      try {
        setIsLoadingTiers(true);
        setTiersError('');
        const response = await getPublicSubscriptionTiersByListingApi(resolvedListingId);
        if (isMounted) {
          setSubscriptionTiers(Array.isArray(response?.tiers) ? response.tiers : []);
        }
      } catch (error) {
        console.error('Failed to fetch public subscription tiers:', error);
        if (isMounted) {
          setSubscriptionTiers([]);
          setTiersError('Unable to load subscription tiers.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingTiers(false);
        }
      }
    };

    fetchSubscriptionTiers();
    return () => {
      isMounted = false;
    };
  }, [activeTab, resolvedListingId, sessionsUnavailable]);

  const handleSubscribeTier = async tier => {
    if (!currentUser) {
      window.location.assign('/login');
      return;
    }
    if (!tier?.id || subscribingTierId) {
      return;
    }

    setSubscribeError('');
    setSubscribingTierId(tier.id);
    try {
      const response = await createSubscriptionCheckoutSessionApi(tier.id);
      const checkoutUrl = response?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Checkout URL was not returned.');
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create subscription checkout session:', error);
      setSubscribeError(error?.error || error?.message || 'Unable to start checkout right now.');
    } finally {
      setSubscribingTierId(null);
    }
  };

  const [giftingTier, setGiftingTier] = useState(null);

  const handleOpenGiftModal = tier => {
    if (!currentUser) {
      window.location.assign('/login');
      return;
    }
    setGiftingTier(tier);
  };

  const getSubscriptionColorClasses = color => {
    if (color === 'teal') {
      return {
        textColorClass: css.subscribeColorTeal,
        backgroundClass: css.subscribeBgTeal,
        lightBackgroundClass: css.subscribeBgLightTeal,
        borderColorClass: css.subscribeBorderTeal,
      };
    }
    if (color === 'amber') {
      return {
        textColorClass: css.subscribeColorAmber,
        backgroundClass: css.subscribeBgAmber,
        lightBackgroundClass: css.subscribeBgLightAmber,
        borderColorClass: css.subscribeBorderAmber,
      };
    }
    return {
      textColorClass: css.subscribeColorPurple,
      backgroundClass: css.subscribeBgPurple,
      lightBackgroundClass: css.subscribeBgLightPurple,
      borderColorClass: css.subscribeBorderPurple,
    };
  };

  const renderSubscriptionPerkIcon = icon => {
    if (icon === 'play') {
      return (
        <path d="M5 4v16l14-8-14-8z" />
      );
    }
    if (icon === 'file') {
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </>
      );
    }
    if (icon === 'message') {
      return (
        <>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </>
      );
    }
    if (icon === 'video') {
      return (
        <>
          <rect x="2" y="6" width="15" height="12" rx="2" ry="2" />
          <polygon points="23 7 17 12 23 17 23 7" />
        </>
      );
    }
    if (icon === 'headphones') {
      return (
        <>
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1v-8h1a2 2 0 0 1 2 2z" />
          <path d="M3 19a2 2 0 0 0 2 2h1v-8H5a2 2 0 0 0-2 2z" />
        </>
      );
    }
    if (icon === 'gift') {
      return (
        <>
          <rect x="3" y="8" width="18" height="13" rx="2" ry="2" />
          <path d="M12 8v13" />
          <path d="M3 12h18" />
          <path d="M12 8H7.5a2.5 2.5 0 1 1 0-5c3.5 0 4.5 5 4.5 5z" />
          <path d="M12 8h4.5a2.5 2.5 0 1 0 0-5c-3.5 0-4.5 5-4.5 5z" />
        </>
      );
    }
    if (icon === 'crown') {
      return (
        <>
          <path d="M3 8l4 4 5-7 5 7 4-4v10H3z" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      );
    }
    return (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </>
    );
  };

  // Get max participants for selected session type
  const maxParticipants = selectedSessionType?.maxParticipants || 1;

  // Get booked slots for the selected session type
  const selectedSessionBookedSlots = useMemo(() => {
    if (!selectedSessionType?._id || !selectedDateString) return [];
    const sessionId = normalizeSessionTypeId(selectedSessionType._id);
    return bookedSlotsMap[sessionId] || [];
  }, [selectedSessionType?._id, selectedDateString, bookedSlotsMap]);

  // Calculate available seats for the selected time slot
  const selectedTimeSlotAvailability = useMemo(() => {
    if (!selectedTimeSlot || !selectedSessionType || !selectedDateString) {
      return { isAvailable: true, availableSeats: maxParticipants, bookedSeats: 0 };
    }
    return getTimeSlotAvailability(selectedTimeSlot, selectedSessionBookedSlots, maxParticipants);
  }, [
    selectedTimeSlot,
    selectedSessionType,
    selectedDateString,
    selectedSessionBookedSlots,
    maxParticipants,
  ]);

  // Get the maximum allowed guest count (limited by available seats for selected time slot)
  const maxAllowedGuests = useMemo(() => {
    if (!selectedTimeSlot || !selectedDateString) {
      // No time slot selected yet, use maxParticipants
      return maxParticipants;
    }
    // Time slot selected, use available seats
    return selectedTimeSlotAvailability.availableSeats;
  }, [
    selectedTimeSlot,
    selectedDateString,
    maxParticipants,
    selectedTimeSlotAvailability.availableSeats,
  ]);

  // Adjust guest count and names when available seats change
  useEffect(() => {
    if (selectedTimeSlot && selectedDateString && guestCount > maxAllowedGuests) {
      // Available seats decreased, adjust guest count
      const newCount = Math.max(1, maxAllowedGuests);
      setGuestCount(newCount);
      // Also adjust guest names array
      setGuestNames((prev) => prev.slice(0, newCount - 1));
    }
  }, [selectedTimeSlot, selectedDateString, maxAllowedGuests, guestCount]);

  const handleDecrement = () => {
    if (guestCount > 1) {
      const newCount = guestCount - 1;
      setGuestCount(newCount);
      // Remove the last guest name
      setGuestNames((prev) => prev.slice(0, -1));
      if (newCount === 1) {
        setIsGuestNamesExpanded(false);
      }
    }
  };

  const handleIncrement = () => {
    if (guestCount < maxAllowedGuests) {
      const newCount = guestCount + 1;
      setGuestCount(newCount);
      // Add a new empty name for the additional guest
      setGuestNames((prev) => [...prev, '']);
      if (newCount > 1 && !isGuestNamesExpanded) {
        setIsGuestNamesExpanded(true);
      }
    }
  };

  const toggleGuestNames = () => {
    setIsGuestNamesExpanded(!isGuestNamesExpanded);
  };

  // Scroll to available times when a date is selected
  useEffect(() => {
    if (selectedDate) {
      // Small timeout to allow the DOM to update and the ref to be attached
      const timer = setTimeout(() => {
        const scrollTarget = availableTimesRef.current || sessionSectionRef.current;
        if (scrollTarget) {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [selectedDate]);

  // Handle time slot selection
  const handleTimeSlotSelect = (sessionType, timeSlot) => {
    const sessionBookedSlots =
      bookedSlotsMap[normalizeSessionTypeId(sessionType._id)] || [];

    // Calculate available seats for this time slot
    const availability = getTimeSlotAvailability(
      timeSlot,
      sessionBookedSlots,
      sessionType.maxParticipants || 1
    );

    // Update state
    setSelectedSessionType(sessionType);
    setSelectedTimeSlot(timeSlot);
    setValidationError(null);

    // Notify parent about session selection for map display
    if (props.onSessionSelect) {
      props.onSessionSelect(sessionType);
    }

    // Limit guest count to available seats
    const availableSeats = availability.availableSeats;
    if (guestCount > availableSeats) {
      setGuestCount(Math.max(1, availableSeats));
    } else if (guestCount < 1) {
      setGuestCount(1);
    }
  };

  const handleDateSelect = (day) => {
    setSelectedDate(day);
    setSelectedTimeSlot(null);
    setGuestCount(1);
    setGuestNames([]);
    setIsGuestNamesExpanded(false);
    setValidationError(null);

    // If a session type was already selected, check if it's still available on this new date
    if (selectedSessionType) {
      const dateString = dateKeyForSelection(day);
      const availableOnNewDate = getSessionTypesForDate(
        dateString,
        [selectedSessionType],
        availabilityData,
        timeZone,
        customerTimeZone
      );

      if (availableOnNewDate.length === 0) {
        // Only reset if not available on the new date
        setSelectedSessionType(null);
        if (props.onSessionSelect) {
          props.onSessionSelect(null);
        }
      }
    }
  };

  const handleMonthChange = (month) => {
    if (onMonthChange) onMonthChange(month);
  };

  // Handle booking submission
  const handleSubmit = async () => {
    setValidationError(null);

    if (!selectedDate) {
      setValidationError('Please select a date to continue');
      return;
    }
    if (!selectedSessionType) {
      setValidationError('Please select a session type to continue');
      return;
    }
    if (!selectedTimeSlot) {
      setValidationError('Please select a time slot to continue');
      return;
    }
    if (!guestCount || guestCount < 1) {
      setValidationError('Please select at least 1 guest to continue');
      return;
    }

    const dateString = dateKeyForSelection(selectedDate);

    const timestamps = getBookingTimestamps(
      dateString,
      selectedTimeSlot,
      selectedSessionType,
      availabilityData,
      timeZone,
      customerTimeZone
    );

    if (!timestamps) {
      setValidationError('Error calculating booking times. Please try again.');
      return;
    }

    const sessionPrice = selectedSessionType.price || 0;
    const totalPriceInSubunits = Math.round(sessionPrice * guestCount * 100);

    if (useCreditForBooking && creditValidation?.canUseCredits && !currentUser?.id?.uuid) {
      setValidationError('Please sign in to use a credit for this booking');
      return;
    }

    const values = {
      bookingDates: {
        startDate: timestamps.bookingStart,
        endDate: timestamps.bookingEnd,
      },
      seats: guestCount.toString(),
      sessionTypeId: selectedSessionType._id,
      sessionTypeName: selectedSessionType.sessionName,
      sessionType: selectedSessionType,
      bookingType: selectedSessionType?.isCampSession
        ? 'camp'
        : selectedSessionType?.availabilityType,
      sessionTypePrice: sessionPrice,
      selectedTimeSlot: selectedTimeSlot,
      guestNames: guestNames.filter(name => !!name.trim()),
      customPrice: totalPriceInSubunits,
      customPricePerUnit: sessionPrice,
      publicData: {
        location: selectedSessionType.location,
      },
      ...(appliedCoupon ? { couponCode: appliedCoupon.code } : {}),
      useCreditForBooking:
        Boolean(useCreditForBooking && creditValidation?.canUseCredits && currentUser?.id?.uuid),
    };

    if (onSubmit) {
      onSubmit(values);
    }
  };

  const canSubmit =
    selectedDate &&
    selectedSessionType &&
    selectedTimeSlot &&
    guestCount >= 1 &&
    (!useCreditForBooking ||
      (!!currentUser?.id?.uuid &&
        creditValidation?.canUseCredits &&
        !loadingCreditValidation));

  // Calculate estimated order total for coupon validation
  const estimatedOrderTotal = useMemo(() => {
    if (!selectedSessionType || !guestCount) return 0;
    return Math.round((selectedSessionType.price || 0) * guestCount * 100); // Convert to cents
  }, [selectedSessionType, guestCount]);

  const bookingPriceBreakdown = useMemo(() => {
    if (!selectedSessionType || !guestCount) {
      return null;
    }

    const baseAmountDollars = (selectedSessionType.price || 0) * guestCount;
    let discountAmountDollars = 0;

    if (appliedCoupon) {
      if (appliedCoupon.type === 'percentage') {
        discountAmountDollars = baseAmountDollars * (appliedCoupon.discount / 100);
      } else if (appliedCoupon.type === 'fixed') {
        discountAmountDollars = Number(appliedCoupon.discount) || 0;
      }
      discountAmountDollars = Math.min(discountAmountDollars, baseAmountDollars);
    }

    const subtotalDollars = baseAmountDollars - discountAmountDollars;
    // Card estimate: under $30 includes the processing fee the server will add.
    const feeBreakdown = calculateBookingFeeBreakdownFromDollars(subtotalDollars, undefined, {
      includeProcessingFee: true,
    });

    return {
      baseAmountDollars,
      discountAmountDollars,
      subtotalDollars,
      feeBreakdown,
    };
  }, [appliedCoupon, selectedSessionType, guestCount]);

  // Prepare orderData for CouponCodeField
  const orderDataForCoupon = useMemo(() => {
    return {
      total: {
        amount: estimatedOrderTotal,
        currency: listing?.attributes?.price?.currency || 'USD',
      },
    };
  }, [estimatedOrderTotal, listing]);

  // Handle coupon applied
  const handleCouponApplied = (coupon) => {
    setAppliedCoupon(coupon);
  };

  // Handle coupon removed
  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
  };

  // Get all dates that should be highlighted as selected in the calendar
  const selectedDates = useMemo(() => {
    const dates = new Set();

    // If we have a total-pricing camp selected, highlight all its dates
    if (selectedSessionType?.isCampSession && selectedSessionType?.pricingType === 'total') {
      const camp = availabilityData.camps[selectedSessionType.campId];
      if (camp) {
        const campDates = calculateCampDates(camp);
        campDates.forEach((d) => dates.add(d));
      }
    } else if (selectedDate) {
      // Otherwise just highlight the single selected date
      dates.add(dateKeyForSelection(selectedDate));
    }

    return dates;
  }, [selectedDate, selectedSessionType, availabilityData.camps, dateKeyForSelection]);

  const classes = classNames(css.root, className);

  return (
    <div className={classes} ref={panelRef} id="booking-panel">
      <div className={css.panelLayout}>
        <div className={css.mainColumn}>
          {/* Tabs */}
          <div className={css.tabsContainer}>
            {!sessionsUnavailable ? (
            <button
              className={classNames(css.tab, { [css.tabActive]: activeTab === 'sessions' })}
              onClick={() => onBookingTabChange('sessions')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={classNames(css.tabIcon, {
                  [css.tabIconSessionsActive]: activeTab === 'sessions',
                })}
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Book Session
            </button>
            ) : null}
            {!sessionsUnavailable ? (
            <button
              className={classNames(css.tab, { [css.tabActive]: activeTab === 'products' })}
              onClick={() => onBookingTabChange('products')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={classNames(css.tabIcon, {
                  [css.tabIconProductsActive]: activeTab === 'products',
                })}
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              Products
            </button>
            ) : null}
            <button
              className={classNames(css.tab, { [css.tabActive]: activeTab === 'subscribe' })}
              onClick={() => onBookingTabChange('subscribe')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={classNames(css.tabIcon, {
                  [css.tabIconSubscribeActive]: activeTab === 'subscribe',
                })}
              >
                <path d="M3 8l4 4 5-7 5 7 4-4v10H3z" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Subscribe
            </button>
          </div>

          {activeTab === 'sessions' ? (
            <>
              {/* Select Date */}
              <div className={css.section}>
                <h2 className={css.sectionTitle}>Select Date</h2>
                <div className={css.calendarContainerLarge}>
                  <CompactCalendar
                    selectedDate={selectedDate}
                    selectedDates={selectedDates}
                    onDateSelect={handleDateSelect}
                    currentMonth={selectedMonth}
                    onMonthChange={handleMonthChange}
                    enabledDates={enabledDates}
                    forceExpanded={true}
                    dateSessionsMap={dateSessionsMap}
                  />
                </div>
              </div>

              {/* Choose Your Session */}
              <div className={css.section} ref={sessionSectionRef}>
                <h3 className={css.sectionTitle}>Choose Your Session</h3>
                <p className={css.sectionSubtitle}>
                  Select the perfect session type for your training
                </p>

                <div className={css.sessionGrid}>
                  {loading ? (
                    <div className={css.loadingMessage}>Loading session types...</div>
                  ) : availableSessionTypesFiltered.length === 0 ? (
                    <div className={css.noSessionsMessage}>No session types available</div>
                  ) : (
                    availableSessionTypesFiltered.map((session, index) => {
                      const isSelected = selectedSessionType?._id === session._id;
                      const isPopular =
                        !!session.popular ||
                        (availableSessionTypesFiltered.length > 2 && index === 0);
                      const maxSpots = session.maxParticipants || 1;
                      const sessionId = normalizeSessionTypeId(session._id);
                      const sessionBookedSlots = session.isCampSession
                        ? aggregateBookedSlotsMap[sessionId] || []
                        : selectedDateString
                          ? bookedSlotsMap[sessionId] || []
                          : aggregateBookedSlotsMap[sessionId] || [];
                      const spotsLeft = getSessionSpotsLeft(sessionBookedSlots, maxSpots);
                      const bookedSpots = Math.max(0, maxSpots - spotsLeft);
                      const isAlmostFull = maxSpots > 1 && spotsLeft <= 2 && spotsLeft > 0;
                      const isFull = spotsLeft === 0;
                      const isSingleSpot = maxSpots === 1;
                      const progressWidth = isSingleSpot
                        ? spotsLeft === 1
                          ? 0
                          : 100
                        : (bookedSpots / maxSpots) * 100;

                      // Icons based on session index or type
                      const getIcon = (idx) => {
                        if (idx % 4 === 0) return <path d="M22 12h-4l-3 9L9 3l-3 9H2" />; // Activity
                        if (idx % 4 === 1)
                          return (
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
                          ); // Users
                        if (idx % 4 === 2)
                          return (
                            <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                          ); // Calendar
                        return <path d="M23 7l-7 5 7 5V7zM1 5h15v14H1V5z" />; // Video
                      };

                      const iconColorClass =
                        index % 4 === 0
                          ? css.iconBlue
                          : index % 4 === 1
                            ? css.iconTeal
                            : index % 4 === 2
                              ? css.iconCoral
                              : css.iconPurple;

                      return (
                        <div
                          key={session._id}
                          className={`${css.sessionCardNew} ${
                            isSelected ? css.sessionCardSelected : ''
                          }`}
                          onClick={() => {
                            if (isSelected) {
                              // Deselect if already selected
                              setSelectedSessionType(null);
                              setSelectedTimeSlot(null);
                              if (props.onSessionSelect) props.onSessionSelect(null);
                            } else {
                              setSelectedSessionType(session);
                              setSelectedTimeSlot(null);
                              if (props.onSessionSelect) props.onSessionSelect(session);
                            }
                          }}
                        >
                          {isPopular && <span className={css.popularBadge}>Most Popular</span>}

                          <div className={css.sessionCardImageContainer}>
                            {session.sessionImage ? (
                              <img
                                src={normalizeCoachifyImageUrl(session.sessionImage)}
                                alt={session.sessionName}
                                className={css.sessionCardImage}
                              />
                            ) : (
                              <div className={css.sessionCardImagePlaceholder} />
                            )}
                            <div className={css.sessionCardImageOverlay} />
                            {isSelected && (
                              <div className={css.selectedCheckmark}>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </div>
                            )}
                          </div>

                          <div className={css.sessionCardBody}>
                            <div className={css.sessionCardHeader}>
                              <div
                                className={classNames(css.sessionIconWrapper, iconColorClass, {
                                  [css.sessionIconWrapperSelected]: isSelected,
                                })}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  {getIcon(index)}
                                </svg>
                              </div>
                               <div className={css.sessionCardTitleBlock}>
                                <h4 className={css.sessionCardTitle}>{session.sessionName}</h4>
                                <Tooltip
                                  text={session.description || 'Professional training session'}
                                  position="top"
                                  containerClassName={css.tooltipWrapper}
                                  className={css.customTooltip}
                                >
                                  <p className={css.sessionCardSubtitle}>
                                    {session.description || 'Professional training session'}
                                  </p>
                                </Tooltip>
                              </div>
                            </div>

                            {/* Spots Available Progress Bar */}
                            <div className={css.spotsProgressSection}>
                              <div className={css.spotsInfo}>
                                <span
                                  className={classNames(css.spotsText, {
                                    [css.spotsTextAlert]:
                                      isAlmostFull || (isSingleSpot && spotsLeft === 0),
                                    [css.spotsTextPrimary]: isSingleSpot && spotsLeft === 1,
                                  })}
                                >
                                  {isSingleSpot
                                    ? spotsLeft === 1
                                      ? '1 spot available'
                                      : 'Fully booked'
                                    : `${spotsLeft} of ${maxSpots} spots left`}
                                </span>
                                {isAlmostFull && !isSingleSpot && (
                                  <span className={css.almostFullBadge}>Almost Full</span>
                                )}
                                {isSingleSpot && spotsLeft === 1 && (
                                  <span className={css.privateBadge}>Private</span>
                                )}
                              </div>
                              <div className={css.progressBarBg}>
                                <div
                                  className={classNames(css.progressBarFill, {
                                    [css.progressFillAlert]:
                                      isAlmostFull || (isSingleSpot && spotsLeft === 0),
                                  })}
                                  style={{ width: `${progressWidth}%` }}
                                />
                              </div>
                            </div>

                            <div className={css.sessionCardFooter}>
                              <span className={css.sessionCardDuration}>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                {session.sessionLength ? `${session.sessionLength} min` : '60 min'}
                              </span>
                              <span className={css.sessionCardPrice}>${session.price}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Time Slots (Visible only when date is selected) - Aggregated from all sessions */}
              {selectedDate && (
                <div className={css.section} ref={availableTimesRef}>
                  <h3 className={css.timeSlotsTitle}>Available Times</h3>
                  {(() => {
                    // Determine which session(s) to show times for
                    const sessionsToShow = selectedSessionType
                      ? [selectedSessionType]
                      : availableSessionTypesFiltered;

                    // Collect available time slots for the chosen sessions
                    const sessionGroups = sessionsToShow
                      .map((st) => {
                        const sessionTypeSlots = getTimeSlotsForSessionType(
                          st,
                          availabilityData,
                          selectedDateString,
                          timeZone,
                          customerTimeZone
                        );

                        const sessionBookedSlots = bookedSlotsMap[normalizeSessionTypeId(st._id)] || [];
                        const availableSlots = filterAvailableTimeSlots(
                          sessionTypeSlots,
                          sessionBookedSlots,
                          st.maxParticipants || 1
                        );

                        return {
                          sessionType: st,
                          availableSlots,
                        };
                      })
                      .filter((group) => group.availableSlots.length > 0);

                    // Sort groups by earliest time slot
                    sessionGroups.sort((a, b) => {
                      const timeA = parseTime(a.availableSlots[0].split(' - ')[0]);
                      const timeB = parseTime(b.availableSlots[0].split(' - ')[0]);
                      return timeA.diff(timeB);
                    });

                    return sessionGroups.length > 0 ? (
                      <div className={css.timeSlotsContainer}>
                        {sessionGroups.map((group) => (
                          <div key={group.sessionType._id} className={css.sessionTimeGroup}>
                            {!selectedSessionType && (
                              <h4 className={css.sessionGroupTitle}>
                                {group.sessionType.sessionName}
                              </h4>
                            )}
                            <div className={css.timeSlotsGridNew}>
                              {group.availableSlots.map((slot, index) => {
                                const isSelected =
                                  selectedTimeSlot === slot &&
                                  selectedSessionType?._id === group.sessionType._id;

                                return (
                                  <button
                                    key={`${group.sessionType._id}-${index}`}
                                    type="button"
                                    className={`${css.timeSlotNew} ${
                                      isSelected ? css.timeSlotNewSelected : ''
                                    }`}
                                    onClick={() => handleTimeSlotSelect(group.sessionType, slot)}
                                  >
                                    <span className={css.slotTime}>{slot}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={css.noTimeSlotsMessage}>
                        No sessions available for this date
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          ) : activeTab === 'subscribe' ? (
            <div className={css.subscribeTabContent}>
              <div className={css.subscribeHeaderRow}>
                <h2 className={css.subscribeTitle}>Subscribe to {ensuredAuthor?.attributes?.profile?.displayName || 'Coach'}</h2>
                <span className={css.subscribeMonthlyBadge}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  </svg>
                  Monthly
                </span>
              </div>
              <p className={css.subscribeSubtitle}>
                Get exclusive content, perks, and discounts with a monthly membership
              </p>

              {subscribeError ? <p className={css.subscribeErrorMessage}>{subscribeError}</p> : null}

              <div className={css.subscribeCardsGrid}>
                {isLoadingTiers ? (
                  [0, 1].map(index => (
                    <div key={`tier-skeleton-${index}`} className={css.subscribeCard}>
                      <div className={classNames(css.subscribeCardInner, css.subscribeCardInnerLoading)}>
                        <div className={classNames(css.subscribeImageWrap, css.subscribeSkeletonBlock)} />
                        <div className={css.subscribeCardBody}>
                          <div className={css.subscribeCardHeader}>
                            <div className={css.subscribeSkeletonColumn}>
                              <div className={classNames(css.subscribeSkeletonLine, css.subscribeSkeletonTitle)} />
                              <div className={classNames(css.subscribeSkeletonLine, css.subscribeSkeletonSubtitle)} />
                            </div>
                            <div className={css.subscribeSkeletonColumn}>
                              <div className={classNames(css.subscribeSkeletonLine, css.subscribeSkeletonPrice)} />
                              <div className={classNames(css.subscribeSkeletonLine, css.subscribeSkeletonSmall)} />
                            </div>
                          </div>
                          <div className={css.subscribePerksGrid}>
                            {[0, 1, 2, 3].map(perkIndex => (
                              <div
                                key={`perk-skeleton-${index}-${perkIndex}`}
                                className={classNames(css.subscribeSkeletonLine, css.subscribeSkeletonSmall)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : tiersError ? (
                  <div className={css.subscribeStateCard}>Unable to load subscription tiers.</div>
                ) : subscriptions.length === 0 ? (
                  <div className={css.subscribeStateCard}>No subscription tiers available yet.</div>
                ) : (
                  subscriptions.map(sub => {
                  const { textColorClass, backgroundClass, lightBackgroundClass, borderColorClass } =
                    getSubscriptionColorClasses(sub.color);
                  return (
                    <div
                      key={sub.id}
                      className={classNames(css.subscribeCard, {
                        [css.subscribeCardPopular]: sub.popular,
                        [borderColorClass]: sub.popular,
                      })}
                    >
                      <div className={css.subscribeCardInner}>
                        <div className={css.subscribeImageWrap}>
                          {sub.image ? (
                            <img
                              src={sub.image}
                              alt={`${sub.name} tier image`}
                              className={css.subscribeImage}
                            />
                          ) : (
                            <div className={css.subscribeImagePlaceholder} />
                          )}
                          <div className={css.subscribeImageOverlay} />
                          {sub.popular ? (
                            <span className={classNames(css.subscribePopularBadge, backgroundClass)}>
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 2l2.8 5.7L21 8.6l-4.5 4.4L17.6 20 12 17.1 6.4 20l1.1-7-4.5-4.4 6.2-.9z" />
                              </svg>
                              Most Popular
                            </span>
                          ) : null}
                        </div>
                        <div className={css.subscribeCardBody}>
                          <div className={css.subscribeCardHeader}>
                            <div>
                              <div className={css.subscribePlanNameRow}>
                                <h3 className={css.subscribePlanTitle}>{sub.name}</h3>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={textColorClass}
                                >
                                  <path d="M3 8l4 4 5-7 5 7 4-4v10H3z" />
                                  <line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                              </div>
                              <p className={css.subscribePlanDescription}>{sub.description}</p>
                            </div>
                            <div className={css.subscribePriceBlock}>
                              <div className={css.subscribePriceRow}>
                                <span className={classNames(css.subscribePrice, textColorClass)}>
                                  ${Number.isInteger(sub.price) ? sub.price : sub.price.toFixed(2)}
                                </span>
                                {sub.billingType !== 'one_time' ? (
                                  <span className={css.subscribePerMonth}>
                                    {billingIntervalSuffix(sub)}
                                  </span>
                                ) : null}
                              </div>
                              <p className={css.subscribeMembers}>
                                {sub.subscribers} {sub.subscribers === 1 ? 'member' : 'members'}
                              </p>
                            </div>
                          </div>

                          <div className={css.subscribePerksGrid}>
                            {sub.perks.map((perk, index) => (
                              <div key={`${sub.id}-${index}`} className={css.subscribePerkItem}>
                                <span
                                  className={classNames(
                                    css.subscribePerkIconWrap,
                                    lightBackgroundClass
                                  )}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className={textColorClass}
                                  >
                                    {renderSubscriptionPerkIcon(perk.icon)}
                                  </svg>
                                </span>
                                <span className={css.subscribePerkText}>{perk.text}</span>
                              </div>
                            ))}
                          </div>

                          <div className={css.subscribeActions}>
                            <button
                              type="button"
                              className={classNames(css.subscribePrimaryButton, {
                                [css.subscribePopularButton]: sub.popular,
                                [backgroundClass]: sub.popular,
                                [css.subscribePrimaryButtonDisabled]: subscribingTierId === sub.id,
                              })}
                              onClick={() => handleSubscribeTier(sub)}
                              disabled={subscribingTierId === sub.id}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                              {subscribingTierId === sub.id
                                ? 'Redirecting...'
                                : sub.billingType === 'one_time'
                                  ? 'Purchase'
                                  : 'Subscribe Now'}
                            </button>
                            <Tooltip
                              text="Gift this to a friend"
                              position="top"
                              containerClassName={css.subscribeGiftTooltipWrap}
                              className={css.subscribeGiftTooltip}
                            >
                              <button
                                type="button"
                                className={css.subscribeSecondaryButton}
                                onClick={() => handleOpenGiftModal(sub)}
                                title="Gift this to a friend"
                                aria-label={`Gift ${sub.name}`}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M20 12v10H4V12" />
                                  <path d="M2 7h20v5H2z" />
                                  <path d="M12 22V7" />
                                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                </svg>
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className={css.productsTabContent}>
              <h2>Coming Soon</h2>
              <p>Products will be available for purchase soon!</p>
            </div>
          )}

          {/* Injected sections (Map, Reviews) */}
          {children}
        </div>

        {!sessionsUnavailable ? (
        <div className={css.orderSidebar}>
          <div className={css.orderCard}>
            <div className={css.orderCardPriceContainer}>
              <span className={css.orderCardPrice}>
                $
                {selectedSessionType?.price ||
                  (listing?.attributes?.price?.amount / 100 || 0).toFixed(0)}
              </span>
              <span className={css.orderCardPerSession}>/ session</span>
            </div>
            {/* <div className={css.orderCardRating}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFB800">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className={css.ratingValue}>{listing?.attributes?.publicData?.rating || '5.0'}</span>
              <span className={css.reviewCount}>· {listing?.attributes?.publicData?.reviewCount || 0} reviews</span>
            </div> */}

            {/* Coupons and Guests */}
            {selectedSessionType && (
              <div className={css.orderDetailsSection}>
                <div className={css.sessionSummary}>
                  <div className={css.sessionSummaryInfo}>
                    {selectedSessionType.sessionImage && (
                      <img
                        src={normalizeCoachifyImageUrl(selectedSessionType.sessionImage)}
                        alt={selectedSessionType.sessionName}
                        className={css.sessionSummaryImage}
                      />
                    )}
                    <div className={css.rightSection}>
                      <span className={css.sessionSummaryName}>
                        {selectedSessionType.sessionName}
                      </span>
                      <span className={css.sessionSummaryMeta}>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {selectedSessionType.sessionLength
                          ? `${selectedSessionType.sessionLength} min session`
                          : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedTimeSlot && (
                  <div className={css.spotsLeftIndicator}>
                    <span>Available spots for this time</span>
                    <span className={css.spotsLeftValue}>
                      {maxAllowedGuests} {maxAllowedGuests === 1 ? 'spot' : 'spots'} left
                    </span>
                  </div>
                )}

                <div className={css.guestSelectorCompact}>
                  <span className={css.guestLabelCompact}>Guests</span>
                  <div className={css.guestControlsCompact}>
                    <button onClick={handleDecrement} disabled={guestCount <= 1}>
                      −
                    </button>
                    <span>{guestCount}</span>
                    <button onClick={handleIncrement} disabled={guestCount >= maxAllowedGuests}>
                      +
                    </button>
                  </div>
                </div>

                {selectedSessionType.discountApplicable !== false && (
                  <div className={css.couponCompact}>
                    <CouponCodeField
                      listingId={listingId}
                      orderData={orderDataForCoupon}
                      appliedCoupon={appliedCoupon}
                      onCouponApplied={handleCouponApplied}
                      onCouponRemoved={handleCouponRemoved}
                      disabled={isOwnListing}
                      sessionTypeId={selectedSessionType._id}
                    />
                  </div>
                )}
              </div>
            )}

            {selectedTimeSlot && bookingPriceBreakdown ? (
              <div className={css.priceBreakdown}>
                <div className={css.breakdownItem}>
                  <span className={css.breakdownLabel}>
                    ${selectedSessionType.price} x {guestCount}{' '}
                    {guestCount > 1 ? 'guests' : 'guest'}
                  </span>
                  <span className={css.breakdownValue}>
                    ${bookingPriceBreakdown.baseAmountDollars.toFixed(2)}
                  </span>
                </div>
                {bookingPriceBreakdown.discountAmountDollars > 0 ? (
                  <div className={classNames(css.breakdownItem, css.breakdownItemDiscount)}>
                    <span className={css.breakdownLabel}>
                      Discount
                      {appliedCoupon?.code ? ` (${appliedCoupon.code})` : ''}
                    </span>
                    <span className={css.breakdownValue}>
                      −${bookingPriceBreakdown.discountAmountDollars.toFixed(2)}
                    </span>
                  </div>
                ) : null}
                {bookingPriceBreakdown.feeBreakdown?.processingFeeAmountCents > 0 ? (
                  <div className={css.breakdownItem}>
                    <span className={css.breakdownLabel}>{PROCESSING_FEE_LABEL}</span>
                    <span className={css.breakdownValue}>
                      {formatCentsAsDollars(
                        bookingPriceBreakdown.feeBreakdown.processingFeeAmountCents
                      )}
                    </span>
                  </div>
                ) : null}
                <div className={css.breakdownItem}>
                  <span className={css.breakdownLabel}>
                    {platformFeeLabel(bookingPriceBreakdown.feeBreakdown?.bookingFeePercent)}
                  </span>
                  <span className={css.breakdownValue}>
                    {formatCentsAsDollars(
                      bookingPriceBreakdown.feeBreakdown?.bookingFeeAmountCents
                    )}
                  </span>
                </div>
                <div className={css.breakdownSeparator} />
                <div className={css.breakdownTotal}>
                  <span className={css.totalLabel}>Total</span>
                  <span className={css.totalValue}>
                    {formatCentsAsDollars(bookingPriceBreakdown.feeBreakdown?.totalAmountCents)}
                  </span>
                </div>
                {creditValidation?.canUseCredits ? (
                  <>
                    <label
                      className={classNames(css.creditToggle, {
                        [css.creditToggleDisabled]: !currentUser,
                      })}
                    >
                      <input
                        type="checkbox"
                        checked={useCreditForBooking}
                        onChange={e => setUseCreditForBooking(e.target.checked)}
                        disabled={!currentUser || loadingCreditValidation || isOwnListing}
                      />
                      <span>Use 1 credit for this booking</span>
                    </label>
                    <div className={css.creditToggleHint}>
                      You have {Number(creditValidation?.creditsRemaining || 0)} credits available with this coach.
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <div className={css.readyToBookCard}>
                <div className={css.bookingIconContainer}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#00a699"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <h4 className={css.readyToBookTitle}>Ready to book?</h4>
                <p className={css.readyToBookSubtitle}>
                  Select a session type and time slot above to get started.
                </p>
              </div>
            )}

            <button
              type="button"
              className={classNames(css.submitButtonNew, {
                [css.submitButtonNewDisabled]: !canSubmit || isOwnListing,
              })}
              onClick={handleSubmit}
              disabled={!canSubmit || isOwnListing}
            >
              {isOwnListing
                ? 'You cannot book your own'
                : !selectedSessionType
                  ? 'Check Availability'
                  : !selectedDate
                    ? 'Select Date'
                    : !selectedTimeSlot
                      ? 'Select Time'
                      : 'Book Now'}
            </button>

            {validationError && <div className={css.validationErrorCompact}>{validationError}</div>}
          </div>

          {/* Things to know */}
          <div className={css.thingsToKnow}>
            <h4 className={css.thingsToKnowTitle}>Things to know</h4>

            {/* Guest Requirements */}
            <div className={css.infoItem}>
              <div className={css.infoItemIcon}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className={css.infoItemContent}>
                <span className={css.infoItemLabel}>Guest Requirements</span>
                <span className={css.infoItemValue}>
                  {listingGuestRequirements || 'No specific requirements'}
                </span>
              </div>
            </div>

            {/* Accessibility */}
            <div className={css.infoItem}>
              <div className={css.infoItemIcon}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className={css.infoItemContent}>
                <span className={css.infoItemLabel}>Accessibility</span>
                <span className={css.infoItemValue}>
                  {listingAccessibility || 'Contact coach for details'}
                </span>
              </div>
            </div>
          </div>
        </div>
        ) : null}
      </div>

      {/* Mobile Bottom Bar */}
      {!sessionsUnavailable ? (
      <>
      <div className={css.mobileBottomBar}>
        <div className={css.mobileBottomBarInner}>
          {selectedTimeSlot && creditValidation?.canUseCredits ? (
            <div className={css.mobileCreditSection}>
              <label
                className={classNames(css.creditToggle, {
                  [css.creditToggleDisabled]: !currentUser,
                })}
              >
                <input
                  type="checkbox"
                  checked={useCreditForBooking}
                  onChange={e => setUseCreditForBooking(e.target.checked)}
                  disabled={
                    !currentUser || loadingCreditValidation || isOwnListing
                  }
                />
                <span>Use 1 credit for this booking</span>
              </label>
              <div className={css.creditToggleHint}>
                You have {Number(creditValidation?.creditsRemaining || 0)} credits available with this coach.
              </div>
            </div>
          ) : null}
          <div className={css.mobileBottomBarPriceBlock}>
            <div className={css.mobileBottomBarPriceValue}>
              <span className={css.mobilePriceBig}>
                $
                {selectedSessionType?.price ||
                  (listing?.attributes?.price?.amount / 100 || 0).toFixed(0)}
              </span>
              <span className={css.mobilePricePerSession}>/ session</span>
            </div>
            <div className={css.mobileBottomBarRating}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFB800">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className={css.mobileRatingValue}>
                {reviewCount > 0 ? rating : '5.0'}
              </span>
              <span className={css.mobileReviewCount}>
                ({reviewCount})
              </span>
            </div>
          </div>
          <button
            type="button"
            className={classNames(css.mobileSubmitButton, {
              [css.mobileSubmitButtonDisabled]: !canSubmit || isOwnListing,
            })}
            onClick={handleSubmit}
            disabled={!canSubmit || isOwnListing}
          >
            {isOwnListing
                ? 'Unavailable'
                : !canSubmit
                  ? 'Select Session'
                  : 'Book Now'}
          </button>
        </div>
      </div>

      {/* Spacer so mobile bar doesn't overlap footer content */}
      <div className={css.mobileSpacer} />
      </>
      ) : null}
      <GiftSubscriptionModal
        isOpen={Boolean(giftingTier)}
        onClose={() => setGiftingTier(null)}
        tier={giftingTier}
      />
    </div>
  );
};

CustomBookingPanel.propTypes = {
  className: PropTypes.string,
  listing: PropTypes.object.isRequired,
  author: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  isOwnListing: PropTypes.bool,
  onSessionSelect: PropTypes.func,
  currentUser: PropTypes.object,
  listingIdOverride: PropTypes.string,
  sessionsUnavailable: PropTypes.bool,
};

export default CustomBookingPanel;
